import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import DatePicker from "./DatePicker";
import api from "../api/axios";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import { downloadBlobFile } from "../utils/fileHelper";

const ExportLogsModal = ({ show, onHide, onExport }) => {
  const { isDark } = useTheme();
  const { userTeamData } = useMicrosoftAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    startDate: null,
    endDate: null
  });

  // Disable body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  const handleDateRangeChange = (start, end) => {
    setExportDateRange({ startDate: start, endDate: end });
  };

  const handleExport = async (dateRange) => {
    try {
      setIsLoading(true);
      const { startDate, endDate } = dateRange;

      // Validate date range
      if (!startDate || !endDate) {
        toast.error("Please select both start and end dates");
        return;
      }

      // Convert dates to ISO string and remove timezone offset
      const formatDate = (date) => {
        return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
      };

      const requestData = {
        startDate: formatDate(new Date(startDate)),
        endDate: formatDate(new Date(endDate)),
        user_role_id: userTeamData?.user_role_id
      };

      // Make the API call to export logs
      const response = await api.post('/submissions/export/logs', requestData, {
        responseType: 'blob', // Important for file download
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/csv',
        },
        timeout: 180000 // 3 minutes
      });

      // Create a download link and trigger download
      downloadBlobFile(response.data, `submission_logs_${new Date().toISOString().split('T')[0]}.csv`, 'csv');

      // Close the modal after successful export
      onHide();
    } catch (error) {
       if (error.code === 'ECONNABORTED') {
        toast.error("Export timed out. Please try again with a shorter date range.");
      } else {
        toast.error("Failed to export logs. Please try again.");
      }
      onHide();
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      setExportDateRange({ startDate: null, endDate: null });
    }
  };

  if (!show) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={onHide}
    >
      {/* Modal Container */}
      <div
        className={`rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] p-[20px] flex flex-col gap-[16px] w-[427px] max-w-[90vw] overflow-visible transition-colors duration-200 ${
          isDark ? 'bg-[#161515]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-[16px]">
          <div className="flex-1 flex items-center gap-[6px]">
            <div
              className="w-[16px] h-[16px] transition-colors duration-200"
              style={{
                backgroundColor: isDark ? '#b3b3b3' : '#072837',
                maskImage: 'url(/assets/icons/export.svg)',
                WebkitMaskImage: 'url(/assets/icons/export.svg)',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            />
            <p className={`font-semibold text-[16px] m-0 transition-colors duration-200 ${
              isDark ? 'text-white' : 'text-[#072837]'
            }`}>
              Export Submission Logs
            </p>
          </div>
          <button
            onClick={onHide}
            className={`flex items-center justify-center w-[20px] h-[20px] p-0 rounded-full border-0 cursor-pointer transition-colors ${
              isDark ? 'bg-[#292929] hover:bg-[#3d3d3d]' : 'bg-[#686868] hover:bg-[#4b5563]'
            }`}
            aria-label="Close"
          >
            <X className="w-[14px] h-[14px] text-white" strokeWidth={2.5} />
          </button>
        </div>

        {/* Date Range Section */}
        <div className="flex flex-col gap-[6px]">
          <p className={`font-normal text-[16px] m-0 transition-colors duration-200 ${
            isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
          }`}>
            Select Date Range
          </p>
          <div className="w-full">
            <DatePicker
              intiStartDate={exportDateRange.startDate}
              intiEndDate={exportDateRange.endDate}
              onDateChange={handleDateRangeChange}
              isDark={isDark}
            />
          </div>
        </div>

        {/* Export Button */}
        <div className="flex">
          <button
            className={`rounded-[8px] px-[16px] py-[8px] font-semibold text-[16px] border-0 cursor-pointer hover:bg-[#b85f24] transition-colors flex items-center justify-center gap-[8px] ${
              isDark ? 
              'bg-[#cd6d2c] hover:bg-[#ffffff] text-[#000000] ' : 'bg-[#cd6d2c] hover:bg-[#072837] text-white '
            }`}
            onClick={() => handleExport(exportDateRange)}
          > 
            {isLoading ? (
              <>
                <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${
                  isDark ? 'border-[#000000]' : 'border-white'
                }`} />
                Exporting...
              </>
            ) : (
              'Export Logs'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ExportLogsModal;
